/**
 * Allocation Bar Component
 *
 * Displays a horizontal stacked bar showing equity/debt/hybrid allocation
 * with proportional widths and embedded labels.
 *
 * Used in holding detail views to visualize asset class breakdown.
 */

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

// ============================================================================
// Types
// ============================================================================

/**
 * Asset class types for allocation
 */
export type AssetClass = "equity" | "debt" | "hybrid" | "cash" | "other";

/**
 * Individual allocation segment data
 */
export interface AllocationSegment {
  /** Asset class type */
  assetClass: AssetClass;
  /** Allocation percentage (0-100) */
  percentage: number;
  /** Optional label override */
  label?: string;
}

/**
 * Props for the AllocationBar component
 */
export interface AllocationBarProps {
  /**
   * Allocation data segments
   * Total of all percentages should equal 100
   */
  allocations: AllocationSegment[];
  /**
   * Height of the bar in pixels
   * @default 32
   */
  height?: number;
  /**
   * Minimum percentage to show label (segments below this threshold won't show embedded labels)
   * @default 10
   */
  minLabelThreshold?: number;
  /**
   * Whether to show a legend below the bar
   * @default true
   */
  showLegend?: boolean;
  /**
   * Optional CSS class name
   */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Asset class configuration with colors and labels
 */
const ASSET_CLASS_CONFIG: Record<
  AssetClass,
  {
    label: string;
    bgColor: string;
    textColor: string;
    legendDotColor: string;
  }
> = {
  equity: {
    label: "Equity",
    bgColor: "bg-blue-500",
    textColor: "text-white",
    legendDotColor: "bg-blue-500",
  },
  debt: {
    label: "Debt",
    bgColor: "bg-emerald-500",
    textColor: "text-white",
    legendDotColor: "bg-emerald-500",
  },
  hybrid: {
    label: "Hybrid",
    bgColor: "bg-violet-500",
    textColor: "text-white",
    legendDotColor: "bg-violet-500",
  },
  cash: {
    label: "Cash",
    bgColor: "bg-slate-400",
    textColor: "text-white",
    legendDotColor: "bg-slate-400",
  },
  other: {
    label: "Other",
    bgColor: "bg-gray-500",
    textColor: "text-white",
    legendDotColor: "bg-gray-500",
  },
};

/**
 * Order of asset classes for consistent display
 */
const ASSET_CLASS_ORDER: AssetClass[] = [
  "equity",
  "debt",
  "hybrid",
  "cash",
  "other",
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Sort allocations by the predefined asset class order
 */
function sortAllocations(allocations: AllocationSegment[]): AllocationSegment[] {
  return [...allocations].sort((a, b) => {
    return (
      ASSET_CLASS_ORDER.indexOf(a.assetClass) -
      ASSET_CLASS_ORDER.indexOf(b.assetClass)
    );
  });
}

/**
 * Filter out zero-percentage allocations
 */
function filterNonZeroAllocations(
  allocations: AllocationSegment[]
): AllocationSegment[] {
  return allocations.filter((a) => a.percentage > 0);
}

// ============================================================================
// Subcomponents
// ============================================================================

/**
 * Individual segment within the stacked bar
 */
function AllocationSegmentBar({
  segment,
  minLabelThreshold,
  isFirst,
  isLast,
}: {
  segment: AllocationSegment;
  minLabelThreshold: number;
  isFirst: boolean;
  isLast: boolean;
}) {
  const config = ASSET_CLASS_CONFIG[segment.assetClass];
  const showLabel = segment.percentage >= minLabelThreshold;

  return (
    <div
      className={cn(
        config.bgColor,
        "flex items-center justify-center transition-all duration-300",
        isFirst && "rounded-l-lg",
        isLast && "rounded-r-lg"
      )}
      style={{ width: `${Math.max(segment.percentage, 1)}%` }}
      title={`${segment.label ?? config.label}: ${segment.percentage.toFixed(1)}%`}
      role="img"
      aria-label={`${segment.label ?? config.label}: ${segment.percentage.toFixed(1)}%`}
    >
      {showLabel && (
        <span
          className={cn(
            "text-xs font-medium truncate px-1",
            config.textColor
          )}
        >
          {segment.label ?? config.label} {segment.percentage.toFixed(0)}%
        </span>
      )}
    </div>
  );
}

/**
 * Legend showing asset class colors and percentages
 */
function AllocationLegend({
  allocations,
}: {
  allocations: AllocationSegment[];
}) {
  const sortedAllocations = sortAllocations(allocations);

  return (
    <div className="flex flex-wrap gap-x-4 gap-y-2 mt-3">
      {sortedAllocations.map((segment) => {
        const config = ASSET_CLASS_CONFIG[segment.assetClass];
        return (
          <div
            key={segment.assetClass}
            className="flex items-center gap-1.5"
          >
            <div
              className={cn(config.legendDotColor, "h-2.5 w-2.5 rounded-full")}
            />
            <span className="text-sm text-muted-foreground">
              {segment.label ?? config.label}
            </span>
            <span className="text-sm font-medium text-foreground">
              {segment.percentage.toFixed(1)}%
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Allocation Bar
 *
 * Displays a horizontal stacked bar chart showing asset allocation
 * breakdown by equity, debt, hybrid, cash, and other categories.
 *
 * @example
 * ```tsx
 * <AllocationBar
 *   allocations={[
 *     { assetClass: "equity", percentage: 65 },
 *     { assetClass: "debt", percentage: 25 },
 *     { assetClass: "cash", percentage: 10 },
 *   ]}
 * />
 * ```
 *
 * @example With custom labels
 * ```tsx
 * <AllocationBar
 *   allocations={[
 *     { assetClass: "equity", percentage: 70, label: "Stocks" },
 *     { assetClass: "debt", percentage: 30, label: "Bonds" },
 *   ]}
 *   showLegend={false}
 * />
 * ```
 */
export function AllocationBar({
  allocations,
  height = 32,
  minLabelThreshold = 10,
  showLegend = true,
  className,
}: AllocationBarProps) {
  // Filter and sort allocations
  const validAllocations = filterNonZeroAllocations(allocations);
  const sortedAllocations = sortAllocations(validAllocations);

  // Handle empty state
  if (sortedAllocations.length === 0) {
    return (
      <div className={cn("space-y-2", className)}>
        <div
          className="w-full rounded-lg bg-muted flex items-center justify-center"
          style={{ height: `${height}px` }}
        >
          <span className="text-sm text-muted-foreground">
            No allocation data
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-0", className)}>
      {/* Stacked Bar */}
      <div
        className="flex w-full overflow-hidden rounded-lg"
        style={{ height: `${height}px` }}
        role="img"
        aria-label="Asset allocation bar chart"
      >
        {sortedAllocations.map((segment, index) => (
          <AllocationSegmentBar
            key={segment.assetClass}
            segment={segment}
            minLabelThreshold={minLabelThreshold}
            isFirst={index === 0}
            isLast={index === sortedAllocations.length - 1}
          />
        ))}
      </div>

      {/* Legend */}
      {showLegend && <AllocationLegend allocations={sortedAllocations} />}
    </div>
  );
}

// ============================================================================
// Skeleton Loader
// ============================================================================

/**
 * Skeleton loader for AllocationBar
 */
export function AllocationBarSkeleton({
  height = 32,
  showLegend = true,
  className,
}: {
  height?: number;
  showLegend?: boolean;
  className?: string;
}) {
  return (
    <div className={cn("space-y-0", className)}>
      {/* Bar Skeleton */}
      <Skeleton
        className="w-full rounded-lg"
        style={{ height: `${height}px` }}
      />

      {/* Legend Skeleton */}
      {showLegend && (
        <div className="flex flex-wrap gap-x-4 gap-y-2 mt-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <Skeleton className="h-2.5 w-2.5 rounded-full" />
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 w-10" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AllocationBar;
